import React, { useState } from "react";
import { Modal, Tooltip, message } from "antd";
import { Package, Barcode, Cpu, Download } from "lucide-react";
import * as XLSX from "xlsx"; // Import SheetJS for Excel compilation
import "./style.css";
import Button from "../../../common/button/button";
import EpcisHistoryModal from "../epcisHistoryModal";
import { useGetTrackNTraceTagEpcisQuery } from "../../../../redux/services/trackntraceApiSlice";
import { useSelector } from "react-redux";
import { getTenantId } from "../../../../redux/features/auth/authSlice";

interface ProductTagsModalProps {
  open: boolean;
  onClose: () => void;
  data?: {
    upc: string;
    productDescription?: string | null;
    productName?: string | null;
    epcs: string[];
    tagIds: string[];
  } | null;
}

const ProductTagsModal = ({ open, onClose, data }: ProductTagsModalProps) => {
  const [selectedDiID, setSelectedDiID] = useState<string | null>(null);
  const [isEpcisModalVisible, setIsEpcisModalVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const tenantId = useSelector(getTenantId);

  const handleClickRow = (text: string) => {
    setSelectedDiID(text);
    setIsEpcisModalVisible(true);
  };

  type ItemTag = {
    id: number;
    epc: string;
    tagId: string;
  };

  const itemTags: ItemTag[] =
    data?.epcs?.map((epc, index) => ({
      id: index + 1,
      epc,
      tagId: data?.tagIds?.[index] || "-",
    })) || [];

  const epcisQuery = useGetTrackNTraceTagEpcisQuery(
    {
      tenant: tenantId,
      diId: selectedDiID,
    },
    {
      skip: !tenantId,
    },
  );

  // Excel Export Handler Routine
  const handleExportExcel = () => {
    if (!itemTags || itemTags.length === 0) {
      message.warning("No serialization registry records found to export.");
      return;
    }

    try {
      setIsExporting(true);

      // Map data into structured key-value configurations for clean columns
      const exportData = itemTags.map((tag) => ({
        "Serial #": tag.id,
        "Tag ID": tag.tagId,
        "Electronic Product Code (EPC)": tag.epc,
        "Product Description": data?.productDescription || data?.productName || "N/A",
        "Global UPC": data?.upc || "N/A",
      }));

      // Initialize workbook object structure
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Serialized Registries");

      // Set explicit layout column dimensions to prevent truncation wraps
      worksheet["!cols"] = [
        { wch: 10 },  // Serial #
        { wch: 24 },  // Tag ID
        { wch: 42 },  // EPC
        { wch: 35 },  // Product Description
        { wch: 18 },  // Global UPC
      ];

      // Format safe safe-string file identification
      const fileTimestamp = new Date().toISOString().split('T')[0];
      const filename = `Serialized_Registry_${data?.upc || "Export"}_${fileTimestamp}.xlsx`;

      // Trigger structural browser client download action
      XLSX.writeFile(workbook, filename);
      message.success("Spreadsheet registry exported successfully!");
    } catch (error) {
      console.error("Failed to generate Excel file", error);
      message.error("Failed to export registry spreadsheet. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      centered
      className="responsive-product-modal"
      wrapClassName="scTheme"
      footer={[
        <Button 
          key="export" 
          variant="secondary" // Assumed alternative variant for multi-action footer paths
          onClick={handleExportExcel}
          loading={isExporting}
          className="mr-2"
        >
           Download Excel
        </Button>,
        <Button key="close" variant="accent" onClick={onClose}>
          Done
        </Button>,
      ]}
      title={
        <div className="flex items-center gap-3 pb-1">
          <div className="p-2 bg-[rgb(var(--primaryRgb)/0.1)] rounded-xl text-[var(--primary)] shrink-0">
            <Package className="w-5 h-5" />
          </div>
          <div>
            <span className="text-base font-semibold text-slate-900 block leading-tight">
              Serialized Item Registry
            </span>
            <span className="text-xs text-slate-500 font-normal">
              View TagID and EPC instances for this item
            </span>
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full overflow-hidden pt-3">
        {/* Themed Light Mode Product Information Card */}
        <div className="bg-slate-50 text-slate-900 rounded-xl p-4 mb-4 shrink-0 border border-slate-200/60 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <span className="text-[11px] uppercase tracking-wider text-[var(--primary)] block font-bold">
                Product Description
              </span>
              <h3 className="text-sm text-slate-700 font-medium leading-relaxed max-w-md">
                {data?.productDescription ||
                  data?.productName ||
                  "No description provided"}
              </h3>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 sm:min-w-[180px]">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Barcode className="w-3.5 h-3.5 text-[var(--primary)]" />
                <span className="text-[11px] uppercase tracking-wider font-bold">
                  Global UPC
                </span>
              </div>
              <span className="text-sm font-mono tracking-wide text-slate-800 block font-medium">
                {data?.upc || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Dynamic Table Box */}
        <div className="border border-slate-200 rounded-xl overflow-hidden max-h-[480px] flex flex-col bg-white shadow-sm">
          {/* Header Row */}
          <div className="grid grid-cols-12 gap-4 bg-slate-50 border-b border-slate-200 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 sticky top-0 z-10">
            <div className="col-span-4 flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5 text-slate-400" />
              Tag ID
            </div>
            <div className="col-span-8">Electronic Product Code (EPC)</div>
          </div>

          {/* Interactive Data List */}
          <div className="overflow-y-auto flex-1 custom-scrollbar">
            {itemTags.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400 text-sm">
                No serialization registries found.
              </div>
            ) : (
              itemTags.map((tag) => (
                <Tooltip key={tag.id} title="Click to view EPCIS data" mouseEnterDelay={0.5}>
                  <div
                    onClick={() => handleClickRow(tag.tagId)}
                    className="grid grid-cols-12 gap-4 px-4 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-[rgb(var(--primaryRgb)/0.05)] group transition-colors duration-150 cursor-pointer"
                  >
                    {/* Tag ID Block */}
                    <div className="col-span-4 flex items-center">
                      <span className="text-xs font-mono bg-slate-50 group-hover:bg-white group-hover:border-[rgb(var(--primaryRgb)/0.3)] text-slate-700 px-2 py-1 rounded border border-slate-200 transition-colors">
                        {tag.tagId}
                      </span>
                    </div>

                    {/* Copy-Scope Wrapper */}
                    <div className="col-span-8 flex justify-between items-center min-w-0 rounded px-1 -mx-1 hover:bg-white/50">
                      <span className="truncate text-xs font-mono text-slate-600 group-hover:text-[var(--primary)] transition-colors pr-2">
                        {tag.epc}
                      </span>
                    </div>
                  </div>
                </Tooltip>
              ))
            )}
          </div>
        </div>
      </div>
      <EpcisHistoryModal
        visible={isEpcisModalVisible}
        onClose={() => setIsEpcisModalVisible(false)}
        diId={selectedDiID}
        data={epcisQuery?.data}
        loading={epcisQuery?.isFetching}
      />
    </Modal>
  );
};

export default ProductTagsModal;